import React, { useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { useCRMData } from '../../context/CRMDataContext';

const ContactForm = ({ contact, onClose, companyId }) => {
  const { addContact, updateContact, companies } = useCRMData();
  const [formData, setFormData] = useState(() => contact ? {
    ...contact,
    relation_strength: contact.relation_strength
      ?? contact.relationStrength
      ?? contact.relationship_strength
      ?? 3
  } : {
    name: '',
    title: '',
    email: '',
    phone: '',
    company_id: companyId || '',
    role_in_buying: 'Påvirker',
    relation_strength: 3
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (contact) {
      updateContact(contact.contact_id, formData);
    } else {
      addContact(formData);
    }
    onClose();
  };

  const handleChange = (e) => {
    const value = e.target.name === 'relation_strength'
      ? Number(e.target.value)
      : e.target.value;

    setFormData({ ...formData, [e.target.name]: value });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">Navn *</label>
        <div className="mt-1 relative rounded-md shadow-sm">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <SafeIcon icon="👤" className="text-gray-400" />
          </div>
          <input type="text" required name="name" value={formData.name} onChange={handleChange} className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md p-2 border" placeholder="Ola Nordmann" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Tittel</label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SafeIcon icon="💼" className="text-gray-400" />
            </div>
            <input type="text" name="title" value={formData.title} onChange={handleChange} className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md p-2 border" placeholder="Daglig Leder" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Selskap</label>
          <select name="company_id" value={formData.company_id} onChange={handleChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md border">
            <option value="">Velg selskap...</option>
            {companies.map(c => (
              <option key={c.company_id} value={c.company_id}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">E-post</label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SafeIcon icon="📧" className="text-gray-400" />
            </div>
            <input type="email" name="email" value={formData.email} onChange={handleChange} className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md p-2 border" placeholder="ola@bedrift.no" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Telefon</label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SafeIcon icon="📞" className="text-gray-400" />
            </div>
            <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md p-2 border" placeholder="900 00 000" />
          </div>
        </div>
      </div>
      <div className="bg-gray-50 p-3 rounded-md border border-gray-200 mt-4">
        <h4 className="text-xs font-bold text-gray-500 uppercase mb-3">Relasjon & Rolle</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Rolle i kjøpsprosessen</label>
            <select name="role_in_buying" value={formData.role_in_buying} onChange={handleChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md border bg-white">
              <option value="Beslutter">Beslutter (Signerer)</option>
              <option value="Påvirker">Påvirker (Influencer)</option>
              <option value="Bruker">Sluttbruker</option>
              <option value="Portvokter">Portvokter</option>
              <option value="Ukjent">Ukjent</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 flex items-center">
              Relasjonsstyrke (1-5) <SafeIcon icon="⭐" className="w-3 h-3 ml-1 text-yellow-500" />
            </label>
            <div className="mt-2 flex items-center space-x-4">
              <input type="range" min="1" max="5" step="1" name="relation_strength" value={formData.relation_strength} onChange={handleChange} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
              <span className="font-bold text-blue-600 w-6">{formData.relation_strength}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {formData.relation_strength === 1 && 'Ingen relasjon (Kald)'}
              {formData.relation_strength === 3 && 'God dialog'}
              {formData.relation_strength === 5 && 'Champion / Ambassadør'}
            </p>
          </div>
        </div>
      </div>
      <div className="mt-5 sm:mt-6 flex justify-end">
        <button type="button" onClick={onClose} className="mr-3 inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none">
          Avbryt
        </button>
        <button type="submit" className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
          <SafeIcon icon="💾" className="w-4 h-4 mr-2" /> Lagre Kontakt
        </button>
      </div>
    </form>
  );
};

export default ContactForm;